import React, { useEffect, useState } from 'react'
import api, { API_URL } from '../../api'
import BarGraph from '../../components/hospitals/HositalBarGraph'
import "../../assets/css/hospitals/hospitals.css"
import DashboardTotalCard from "../../components/dashboard/DashboardTotalCard";
import DashboardContainer from '../../components/dashboard/DashboardContainer'
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { Timer02Icon, BedIcon, SearchingIcon, WorkoutRunIcon, PlusSignIcon, } from "hugeicons-react";
import { PrimaryButton } from '../../components/forms/buttons'
import AddHospitalPopup from '../../components/hospitals Popup/AddHospitalPopup'
import Hospitals from './Hospitals'
import { hasRole } from '../../services/userPosition'
import TableLoading from '../../components/loading/TableLoading'

const monthToNumber = {
  January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
  July: 7, August: 8, September: 9, October: 10, November: 11, December: 12,
  YTD: 'ytd'
};

// Removed old LoadingOverlay in favor of TableLoading component
const HospitalsPageContent = () => {
  const [hospitals, setHospitals] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [totalData, setTotalData] = useState({});
  // Years are now managed by availableYears state
  const [barData, setBarData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("YTD");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isLoadingYTD, setIsLoadingYTD] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const fetchTotalsData = async (value) => {
    try {
      if (selectedMonth === 'YTD') {
        setIsLoadingYTD(true);
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const endMonth = (Number(value) === currentYear) ? currentMonth : 12;

        let aggregatedData = {
          total_acute: 0,
          total_swing_bed: 0,
          total_observation: 0,
          total_emergency_room: 0
        };

        for (let month = 1; month <= endMonth; month++) {
          try {
            const response = await api.get(`${API_URL}/dashboard/dashboard_totals/${value}/${month}/`);
            // Always treat missing or empty data as zero
            let monthData = (response.status === 200 && response.data[Number(value)]) ? response.data[Number(value)] : {};
            aggregatedData.total_acute += monthData.total_acute || 0;
            aggregatedData.total_swing_bed += monthData.total_swing_bed || 0;
            aggregatedData.total_observation += monthData.total_observation || 0;
            aggregatedData.total_emergency_room += monthData.total_emergency_room || 0;
          } catch (monthError) {
            // If API fails for a month, treat as zero (do nothing)
          }
        }
        setTotalData(aggregatedData);
        setIsLoadingYTD(false);
      } else {
        const monthParam = monthToNumber[selectedMonth] || 0;
        const response = await api.get(`${API_URL}/dashboard/dashboard_totals/${selectedYear}/${monthParam}/`);
        if (response.status === 200) {
          setTotalData(response.data[Number(value)]);
        }
      }
      setIsOverlayVisible(false);
    } catch (error) {
      setIsOverlayVisible(false);
      setIsLoadingYTD(false);
    }
  };

  const handleAddHospital = async (value) => {
    setIsPopupOpen(!isPopupOpen);
  }

  const handlePopupSubmit = (e) => {
    e.preventDefault();
    handleClosePopup();
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };


  const fetchAvailableYears = async () => {
    try {
      const response = await api.get(`${API_URL}/measures/years/`);
      if (response.status === 200) {
        // Correctly extract years from the response
        const years = Array.isArray(response.data.years) ? response.data.years : [];
        const sortedYears = years.sort((a, b) => b - a);
        setAvailableYears(sortedYears);
        // Only set selected year if not already set
        setSelectedYear(prev => (prev === null ? (sortedYears.includes(new Date().getFullYear()) ? new Date().getFullYear() : sortedYears[0]) : prev));
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
      // Fallback to current year and previous years if API fails
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3]);
    }
  };

  // Lazy loading - only load data when user is on analytics tab and data is needed
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    // Only load data if user is on analytics tab and data hasn't been loaded yet
    if (activeTab === 'analytics' && !dataLoaded) {
      const loadData = async () => {
        try {
          setIsOverlayVisible(true);

          // Load critical data first (years)
          await fetchAvailableYears();

          // Load bar data and totals with a small delay to prevent overwhelming the API
          setTimeout(async () => {
            const monthParam = selectedMonth === 'YTD' ? 0 : (monthToNumber[selectedMonth] || 0);
            const response = await api.get(
              `${API_URL}/hospitals/data/`,
              {
                params: {
                  "year": selectedYear || 2024,
                  "month": monthParam
                }
              }
            );

            const result = response.data;
            setBarData(result);
            setDataLoaded(true);
            setIsLoading(false);

            // Load totals data after a delay
            setTimeout(() => {
              fetchTotalsData(selectedYear);
            }, 100);
          }, 100);

        } catch (error) {
          setIsOverlayVisible(false);
          console.error('Error loading hospitals data:', error);
        }
      };

      loadData();
    }
  }, [activeTab, dataLoaded, selectedYear, selectedMonth]);

  // Reset data when user switches tabs
  useEffect(() => {
    if (activeTab !== 'analytics') {
      setDataLoaded(false);
    }
  }, [activeTab]);


  const handleDurationYearChange = (value) => {
    setSelectedYear(value)
  };

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
  };

  if (!barData) {
    return <p>Loading...</p>;
  }

  return isLoading ? 'Getting data..' : (

    <div className='hospitals-page'>
      <div className="header"></div>
      <div className="tabs">
        <div
          className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Hospitals Analytics
        </div>
        <div
          className={`tab ${activeTab === 'allHospitals' ? 'active' : ''}`}
          onClick={() => setActiveTab('allHospitals')}
        >
          All Hospitals
        </div>
      </div>

      <div className='tab-contents'>
        {activeTab === 'analytics' && (
          <div className='select-button'>
            <Select
              onChange={(e) => {
                handleDurationYearChange(e.target.value);
              }}
              name="duration"
              id="duration"
              value={selectedYear}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>

            <Select
              onChange={(e) => {
                handleMonthChange(e.target.value);
              }}
              name="selectedMonth"
              id="selectedMonth"
              value={selectedMonth}
            >
              <MenuItem value="YTD">YTD (Year-to-Date)</MenuItem>
              <MenuItem value="January">January</MenuItem>
              <MenuItem value="February">February</MenuItem>
              <MenuItem value="March">March</MenuItem>
              <MenuItem value="April">April</MenuItem>
              <MenuItem value="May">May</MenuItem>
              <MenuItem value="June">June</MenuItem>
              <MenuItem value="July">July</MenuItem>
              <MenuItem value="August">August</MenuItem>
              <MenuItem value="September">September</MenuItem>
              <MenuItem value="October">October</MenuItem>
              <MenuItem value="November">November</MenuItem>
              <MenuItem value="December">December</MenuItem>
            </Select>

            {
              hasRole(["Admin", "Super User"]) ?
                <PrimaryButton
                  onClick={handleAddHospital}
                  processingText={'Submitting'}
                  iconClass={<PlusSignIcon />}
                  buttonText={'Add Hospital'}
                  type="button"
                />
                :
                <></>
            }

            {isPopupOpen && (
              <AddHospitalPopup
                onClose={handleClosePopup}
                onSubmit={handlePopupSubmit}
              />
            )}
          </div>
        )}

        {((selectedYear && selectedYear !== availableYears[0]) || (selectedMonth && selectedMonth !== 'YTD')) && (
          <div className="filter-summary">
            <span className="filter-label"><h3>Active Filters:</h3></span>
            <div className="filter-tags">
              {(selectedYear && selectedYear !== availableYears[0]) && (
                <span className="filter-tag">
                  <h3>Year:</h3>
                  <span>{selectedYear}</span>
                </span>
              )}
              {(selectedMonth && selectedMonth !== 'YTD') && (
                <span className="filter-tag">
                  <h3>Month:</h3>
                  <span>{selectedMonth}</span>
                </span>
              )}
            </div>
            <p className="clear-all-filters"
              onClick={() => {
                setSelectedYear(availableYears[0]);
                setSelectedMonth('YTD');
              }}>Clear All</p>
          </div>
        )}

        {activeTab === 'analytics' && selectedMonth === 'YTD' && (
          <div className="ytd-indicator">
            {isLoadingYTD ? (
              <>⏳ Loading Year-to-Date data...</>
            ) : (
              <>
                Displaying Year-to-Date data (January - {
                  Number(selectedYear) === new Date().getFullYear()
                    ? new Date().toLocaleString('default', { month: 'long' })
                    : 'December'
                })
              </>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="total-hospital-cards">
            <DashboardTotalCard
              className="total-card"
              icon={
                <Timer02Icon size={24} color={"#07AEEF"} variant={"stroke"} />
              }
              value={
                totalData.total_acute ? (
                  <h2>{totalData.total_acute}</h2>
                ) : (
                  <span>No Available Data</span>
                )
              }
              name={"Total Acute"}
            />
            <DashboardTotalCard
              className="total-card"
              icon={<BedIcon size={24} color={"#FFB60A"} variant={"stroke"} />}
              value={
                totalData.total_swing_bed ? (
                  <h2>{totalData.total_swing_bed}</h2>
                ) : (
                  <span>No Available Data</span>
                )
              }
              name={"Total Swingbed"}
            />
            <DashboardTotalCard
              className="total-card"
              icon={
                <SearchingIcon size={24} color={"#FC7D75"} variant={"stroke"} />
              }
              value={
                totalData.total_observation ? (
                  <h2>{totalData.total_observation}</h2>
                ) : (
                  <span>No Available Data</span>
                )
              }
              name={"Total Observation"}
            />
            <DashboardTotalCard
              className="total-card"
              icon={
                <WorkoutRunIcon
                  size={24}
                  color={"#0E76BC"}
                  variant={"stroke"}
                />
              }
              value={
                totalData.total_emergency_room ? (
                  <h2>{totalData.total_emergency_room}</h2>
                ) : (
                  <span>No Available Data</span>
                )
              }
              name={"Total Emergency Room"}
            />
          </div>
        )}

        {activeTab === 'analytics' ?
          (barData.measures_data !== null ?
            <div className="table-container">
              {isOverlayVisible && <TableLoading />}
              <BarGraph data={barData} year={selectedYear} />
            </div>
            : <p>No Data Available</p>)
          : <></>}
        {activeTab === 'allHospitals' && (
          <div className='tab-contents-hospitals table-container'>
            {isOverlayVisible && <TableLoading />}
            <Hospitals />
          </div>
        )}
      </div>


    </div>
  );

}
const HospitalsPage = () => {
  return (
    <DashboardContainer content={<HospitalsPageContent />} pageTitle={'All hospitals'} />
  )
}
export default HospitalsPage









